import React, { useState, useEffect } from 'react'
import useSWR from 'swr'
import { Popover, PopoverContent, PopoverTrigger, CheckboxV2, Icon } from '@shopflo/ui'
import { CaretDown, GitBranch } from '@phosphor-icons/react'
import { SalesChannelV2 } from 'src/lib/types/discountsV2'
import { apiURI } from 'src/lib/utils/constants'
import { fetcher } from 'src/lib/core/api-client'
import { useLocale } from 'src/lib/hooks/useLocal'

interface GenericChannelSelectorProps {
  selectedChannelIds: string[]
  onChange: (ids: string[]) => void
}

export const GenericChannelSelector = ({ selectedChannelIds, onChange }: GenericChannelSelectorProps) => {
  const [open, setOpen] = useState(false)
  const { t } = useLocale()

  const { data: channels = [] } = useSWR<SalesChannelV2[]>(
    '/merchant-app/listing',
    async (url: string) => {
      const res: any = await fetcher(url, apiURI.AUTH_ENGINE)
      return res?.data || []
    },
    {
      onSuccess: data => {
        if (selectedChannelIds.length === 0 && data?.length > 0) {
          onChange(data?.map(channel => channel?.id))
        }
      }
    }
  )

  const isAllSelected = channels.length > 0 && selectedChannelIds.length === channels.length
  const isIndeterminate = selectedChannelIds.length > 0 && selectedChannelIds.length < channels.length

  const handleToggle = (id: string, checked: boolean) => {
    const nextIds = checked ? [...selectedChannelIds, id] : selectedChannelIds.filter(channelId => channelId !== id)
    onChange(nextIds)
  }

  const handleToggleAll = (checked: boolean) => {
    onChange(checked ? channels.map(channel => channel.id) : [])
  }

  const getLabel = () => {
    const count = selectedChannelIds.length

    if (!channels.length) return 'loading...'
    if (isAllSelected) return 'all channels'
    if (count === 0) return 'no channels'

    return count === 1
      ? (channels.find(channel => channel.id === selectedChannelIds?.[0])?.name ?? '1 channel').toLowerCase()
      : `${count} channels`
  }

  return (
    <div className='flex flex-col w-full px-3 hover:bg-zinc-500/5 rounded-xl'>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className='flex items-center justify-start gap-1.5 py-3 border-none bg-transparent p-0 shadow-none hover:bg-transparent cursor-pointer w-full text-text-sm-regular text-zinc-500'>
            <Icon IconComponent={GitBranch} size='sm' className='text-zinc-400 rotate-180' />
            <span>Available on </span>
            <span className='text-text-sm-semibold text-zinc-600 transition-colors group-hover:text-zinc-700'>
              {getLabel()}
            </span>
            <Icon IconComponent={CaretDown} size={14} className='text-zinc-400' />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align='start'
          className='w-[300px] p-1.5 bg-white !border-none !rounded-xl overflow-hidden'
          sideOffset={8}
        >
          <div className='flex flex-col max-h-[350px] overflow-y-auto'>
            <div
              className='flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-zinc-950/5 cursor-pointer transition-colors'
              onClick={() => handleToggleAll(!isAllSelected)}
            >
              <CheckboxV2
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onCheckedChange={checked => handleToggleAll(!!checked)}
                size='sm'
                onClick={e => e.stopPropagation()}
              />
              <span className='text-text-sm-regular text-zinc-600'>{t('customer_eligibility.all_channels')}</span>
            </div>

            {channels.map(channel => (
              <div
                key={channel.id}
                className='flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-zinc-950/5 cursor-pointer transition-colors group'
                onClick={() => handleToggle(channel.id, !selectedChannelIds.includes(channel.id))}
              >
                <CheckboxV2
                  checked={selectedChannelIds.includes(channel.id)}
                  onCheckedChange={checked => handleToggle(channel.id, !!checked)}
                  size='sm'
                  onClick={e => e.stopPropagation()}
                />
                <div className='flex flex-col'>
                  <span className='text-text-sm-regular text-zinc-600'>{channel.name}</span>
                </div>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
